// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the "Open in new window" menu entry shared by mail, chat and sidebar menus.
// It is left out where new windows don't exist (phones, desktop apps from before it).

import {DataMenuRenderer} from '@app/features/ui/action_menu/DataMenuRenderer';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import {AppWindowIcon} from '@app/features/ui/icons/Icons';
import type {MenuGroupType, MenuItemType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {canOpenNewWindow, openInNewWindow} from '@app/features/window/utils/NewWindow';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import type React from 'react';

export const OPEN_IN_NEW_WINDOW_DESCRIPTOR = msg({
	message: 'Open in new window',
	comment: 'Context menu action: opens this item in another full Konvo window that can be navigated on its own.',
});

/** The entry as a data-menu item, or an empty list where it doesn't apply. */
export function newWindowMenuItems(i18n: I18n, routePath: string, close: () => void): Array<MenuItemType> {
	if (!canOpenNewWindow()) return [];
	return [
		{
			id: 'open_in_new_window',
			label: i18n._(OPEN_IN_NEW_WINDOW_DESCRIPTOR),
			icon: <AppWindowIcon size={18} />,
			onClick: () => {
				close();
				openInNewWindow(routePath);
			},
		},
	];
}

/** The entry in a group of its own, for menus that lead with it. */
export function newWindowMenuGroups(i18n: I18n, routePath: string, close: () => void): Array<MenuGroupType> {
	const items = newWindowMenuItems(i18n, routePath, close);
	return items.length > 0 ? [{items}] : [];
}

/** onContextMenu for a sidebar page button: a menu with just "Open in new window". */
export function openNewWindowContextMenu(event: React.MouseEvent, i18n: I18n, routePath: string): void {
	if (!canOpenNewWindow()) return;
	ContextMenuCommands.openFromEvent(event, ({onClose}) => (
		<DataMenuRenderer groups={newWindowMenuGroups(i18n, routePath, onClose)} />
	));
}

/** A desktop row menu for list rows that have no other actions (Chats' server channel and ClickUp rows). */
export function newWindowRowMenu(
	i18n: I18n,
	routePath: string,
): {open: (event: React.MouseEvent) => void; openSheet: () => void; sheet: null} | undefined {
	if (!canOpenNewWindow()) return undefined;
	return {open: (event) => openNewWindowContextMenu(event, i18n, routePath), openSheet: () => {}, sheet: null};
}
